import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";

import {AbstractControl, FormBuilder, FormGroup, Validators} from "@angular/forms";

/** Services */
import { PopupService } from "../../../services/popup.service";

import { register, login, loginSuccess, loginFailure } from "../../../state/auth/auth.actions";
import { AppState } from "../../../state/app.state";

import {selectRegisterSuccess, selectRegisterFailure, selectAuthState} from '../../../state/auth/auth.selectors';
import { Actions, ofType } from '@ngrx/effects';
import {Subscription, filter, take, skip} from "rxjs";

import { loadCountries } from "../../../state/list/list.actions";
import { selectCountriesSuccess, selectCountriesFailure } from '../../../state/list/list.selectors';


@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit, OnDestroy {

  @Output() toggleCompanyChange = new EventEmitter<boolean>();

  form: FormGroup;
  errorMessages: any;
  isLoading: boolean = true;
  isCompany: boolean = false;
  countries: any[] = [];
  search_id: any = null;
  event: any = null; // Add event property

  private successSubscription: Subscription;
  private failureSubscription: Subscription;

  constructor(
    public formBuilder: FormBuilder,
    private routerService: Router,
    private popupService: PopupService,
    private store: Store<AppState>,
    private activatedRoute: ActivatedRoute,
    private actions$: Actions,
  ) {

    this.form = this.formBuilder.group({
      email: ["", [Validators.required, this.emailValidator.bind(this)]],
      name: ["", [Validators.required, Validators.minLength(3)]],
      linkedin_url: ["", [Validators.required, Validators.pattern(/^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/)]],
      country_id: ["", [Validators.required]],
      company_name: ["", this.isCompany ? [Validators.required] : []],
      company_vat: ["", this.isCompany ? [Validators.required] : []],
      company_country_id: ["", this.isCompany ? [Validators.required] : []],
      company_county: ["", this.isCompany ? [Validators.required] : []],
      company_town: ["", this.isCompany ? [Validators.required] : []],
      company_zip_code: ["", this.isCompany ? [Validators.required] : []],
      company_address: ["", this.isCompany ? [Validators.required] : []],
      company_is_vat_payer: [false],  // No validators needed, defaults to false
      password: ["", [Validators.required, Validators.minLength(8), Validators.maxLength(20), this.passwordStrengthValidator.bind(this)]],
      password_confirmation: ["", [Validators.required]],
      invitation_code: [""],  // Optional field for invitation code
      remember: [false]
    }, {
      validators: this.passwordMatchValidator
    });

    this.errorMessages = {
      name: [
        { type: "required", message: "Name is required" },
        { type: "minlength", message: "Name is too short."},
      ],
      email: [
        { type: "required", message: "Email is required" },
        { type: "email", message: "Must be an email address" }
      ],
      linkedin_url: [
        { type: "required", message: "LinkedIn URL is required" },
        { type: "minlength", message: "LinkedIn URL is too short."},
      ],
      country_id: [
        { type: "required", message: "Country is required" },
      ],
      company_name: [
        { type: "required", message: "Company name is required" },
        { type: "minlength", message: "Company name is too short." },
      ],
      company_vat: [
        { type: "required", message: "VAT is required" },
        { type: "minlength", message: "VAT is too short." },
      ],
      company_country_id: [
        { type: "required", message: "Company country is required" },
      ],
      company_county: [
        { type: "required", message: "Company county is required" },
        { type: "minlength", message: "Company county is too short." },
      ],
      company_town: [
        { type: "required", message: "Company town is required" },
        { type: "minlength", message: "Company town is too short." },
      ],
      company_zip_code: [
        { type: "required", message: "Company zip code is required" },
        { type: "minlength", message: "Company zip code is too short." },
      ],
      company_address: [
        { type: "required", message: "Company address is required" },
        { type: "minlength", message: "Company address is too short." },
      ],
      password: [
        { type: "required", message: "Password is required" },
        { type: "minlength", message: "Password is too short." },
      ],
      /*
      invite_code: [
        {
          type: "required",
          message: "Invite Code is required"
        },
        {
          type: "minlength",
          message: "Invite code is too short"
        }
      ],
      */
      password_confirmation: [
        { type: "required", message: "Password is required" },
        { type: "minlength", message: "Password is too short." },
      ],
    };

    // Listen for search_id and event when registering
    this.activatedRoute.queryParams.subscribe(queryParams => {
      this.search_id = queryParams['search_id'] || null;
      this.event = queryParams['event'] || null;
    });
  }

  passwordMatchValidator(formGroup: FormGroup): { [key: string]: boolean } | null {
    const password = formGroup.get('password');
    const confirmPassword = formGroup.get('password_confirmation');

    if (!password || !confirmPassword) {
      return null; // If either of the fields doesn't exist, return null (no error)
    }

    // If passwords don't match, return the error object
    return password.value === confirmPassword.value ? null : { notMatching: true };
  }

  /**
   * Custom email validator with strict format checking
   * Requires: valid local part + @ + domain with proper TLD
   *
   * @param control - The form control to validate
   * @returns null if valid, error object if invalid
   */
  private emailValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null; // Don't validate empty values (required validator handles that)
    }

    // RFC 5322 compliant regex with proper TLD requirement
    // Pattern explanation:
    // - [a-zA-Z0-9._%+-]+ : Local part (before @) - allows letters, numbers, and common special chars
    // - @ : Required @ symbol
    // - [a-zA-Z0-9.-]+ : Domain name - allows letters, numbers, dots, and hyphens
    // - \.[a-zA-Z]{2,} : TLD with at least 2 characters (e.g., .com, .co, .uk, .info)
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return emailPattern.test(control.value) ? null : { invalidEmail: true };
  }

  /**
   * Custom password validator enforcing complexity requirements
   * Requires: 8-20 characters, at least one letter, one number, and one special character
   *
   * @param control - The form control to validate
   * @returns null if valid, error object with specific validation failures if invalid
   */
  private passwordStrengthValidator(control: AbstractControl): { [key: string]: boolean } | null {
    if (!control.value) {
      return null; // Don't validate empty values (required validator handles that)
    }

    const password = control.value;
    const errors: { [key: string]: boolean } = {};

    // Check for at least one letter (uppercase or lowercase)
    if (!/[a-zA-Z]/.test(password)) {
      errors['noLetter'] = true;
    }

    // Check for at least one number
    if (!/[0-9]/.test(password)) {
      errors['noNumber'] = true;
    }

    // Check for at least one special character
    // Allowed special characters: !@#$%^&*()_+-=[]{};':"\\|,.<>/?
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors['noSpecialChar'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  getCountries(): void {
    this.store.dispatch(loadCountries());

    this.successSubscription = this.store.select(selectCountriesSuccess).pipe(
      filter(response => response !== null),
    ).subscribe({
      next: (response) => {
        this.countries = response.countries;
      }
    });

    this.failureSubscription = this.store.select(selectCountriesFailure).pipe(
      filter(error => error !== null),
    ).subscribe({
      next: () => {
        this.popupService.notify('Failed to load countries. Please refresh the page.', 'Error', 'error');
      }
    });
  }

  ngOnDestroy() {
    this.successSubscription.unsubscribe();
    this.failureSubscription.unsubscribe();
  }

  toggleCompany() {
    this.isCompany = !this.isCompany;
    this.toggleCompanyChange.emit(this.isCompany);

    const companyControls = [
      'company_name', 'company_vat', 'company_country_id', 'company_county',
      'company_town', 'company_zip_code', 'company_address'
    ];

    if (this.isCompany) {
      companyControls.forEach(control => {
        this.form.controls[control].setValidators([Validators.required]);
        this.form.controls[control].updateValueAndValidity();
      });
    } else {
      companyControls.forEach(control => {
        this.form.controls[control].clearValidators();
        this.form.controls[control].reset();  // Clear validators and reset the value
      });
    }
  }

  clearCompany () {
    this.form.controls['company_name'].reset()
    this.form.controls['company_vat'].reset()
    this.form.controls['company_country_id'].reset()
    this.form.controls['company_county'].reset()
    this.form.controls['company_town'].reset()
    this.form.controls['company_zip_code'].reset()
    this.form.controls['company_address'].reset()
    this.form.controls['company_is_vat_payer'].reset()
  }

  checkPasswords(group: FormGroup) {
    const password = group.get('password').value;
    const confirmPassword = group.get('confirmPassword').value;
    return password === confirmPassword ? null : {notSame: true};
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.getCountries()
  }

  /**
   * @desc Register form handler
   */
  processRegister() {
    // Mark all controls as touched to trigger validation messages
    this.form.markAllAsTouched();

    // Get current query parameters directly
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const eventParam = queryParams['event'] || null;
    const searchIdParam = queryParams['search_id'] || null;

    // Debug log to check values
    // console.log('Query params:', queryParams);
    // console.log('Event param:', eventParam);
    // console.log('Search ID param:', searchIdParam);

    let params = {
      name: this.form.value.name,
      email: this.form.value.email,
      linkedin_url: this.form.value.linkedin_url,
      country_id: this.form.value.country_id,
      company_invoice: this.isCompany,
      company_name: this.form.value.company_name,
      company_identifier: this.form.value.company_vat,
      company_country_id: this.form.value.company_country_id,
      company_county: this.form.value.company_county,
      company_town: this.form.value.company_town,
      company_zip_code: this.form.value.company_zip_code,
      company_address: this.form.value.company_address,
      is_tax_payer: this.form.value.company_is_vat_payer,
      password: this.form.value.password,
      password_confirmation: this.form.value.password_confirmation,
      search_id: searchIdParam,
      event: this.form.value.invitation_code || eventParam // Use invitation_code field, fallback to URL param
    };

    // console.log('Registration params:', params);

    if (this.form.valid) {
      // Subscribe to auth state to handle both success and error conditions
      const authStateSub = this.store.select(selectAuthState)
        .pipe(
          skip(1), // Skip current state, react only to new changes after dispatch
          filter(state => (state.user !== null) || (state.error !== null)), // Filter for either success or error
          take(1) // Take only one emission
        )
        .subscribe(state => {
          if (state.error !== null) {
            const errorMessage = this.extractErrorMessage(state.error);
            this.popupService.notify(errorMessage, 'Registration Error', 'error');
          } else if (state.user !== null) {
            this.popupService.notify('You have successfully registered!', 'Registration Success', 'success');
            this.autoLogin(this.form.value.email, this.form.value.password);
          }
        });

      // Dispatch the action after subscription is set up
      this.store.dispatch(register(params));
    }
  }

  /**
   * Auto-login after successful registration and navigate to onboarding
   */
  private autoLogin(email: string, password: string): void {
    this.actions$.pipe(
      ofType(loginSuccess, loginFailure),
      take(1)
    ).subscribe(action => {
      if (action.type === loginSuccess.type) {
        this.routerService.navigate(['/onboarding']);
      } else {
        this.routerService.navigate(['/login']);
      }
    });

    this.store.dispatch(login({ email, password }));
  }

  /**
   * Extracts a user-friendly error message from various error formats
   * Handles API error responses, HTTP errors, and generic error objects
   *
   * @param error - The error object from the API or HTTP response
   * @returns A user-friendly error message string
   */
  private extractErrorMessage(error: any): string {
    if (!error) {
      return 'An unexpected error occurred. Please try again.';
    }

    // Handle string errors directly
    if (typeof error === 'string') {
      return error;
    }

    // Handle nested error structures (e.g., error.error.message)
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (error.error.message) {
        return error.error.message;
      }
      if (error.error.errors) {
        // Handle validation errors array
        const validationErrors = error.error.errors;
        if (Array.isArray(validationErrors)) {
          return validationErrors.join('. ');
        }
        if (typeof validationErrors === 'object') {
          return Object.values(validationErrors).flat().join('. ');
        }
      }
    }

    // Handle direct message property
    if (error.message) {
      return error.message;
    }

    // Handle status text from HTTP errors
    if (error.statusText) {
      return error.statusText;
    }

    // Fallback for unknown error formats
    return 'Registration failed. Please check your information and try again.';
  }
}
